import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, MapPin } from "lucide-react";
import { StopCard } from "@/pages/CrmMyDay";
import { QuickAddProspect } from "@/components/QuickAddProspect";
import { RepPicker } from "@/components/RepPicker";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shift = (s, n) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const pretty = (s) => new Date(`${s}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });

export default function MobileToday() {
  const { user } = useAuth();
  const elevated = ["admin", "manager", "sales_manager"].includes(user?.role);
  const [date, setDate] = useState(iso(new Date()));
  const [repId, setRepId] = useState(user?.id);
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get("/crm/daily-stops", { params: { date, rep_id: repId } })
      .then(({ data }) => setStops(data.stops))
      .catch(() => toast.error("Could not load stops"))
      .finally(() => setLoading(false));
  }, [date, repId, reloadKey]);

  const toggle = async (s) => {
    const done = !s.done;
    setStops((xs) => xs.map((x) => (x.call_id === s.call_id && x.kind === s.kind ? { ...x, done } : x)));
    try { await api.post("/crm/daily-stops/done", { call_id: s.call_id, kind: s.kind, done }); }
    catch { toast.error("Could not update stop"); setReloadKey((k) => k + 1); }
  };
  const doneCount = stops.filter((s) => s.done).length;
  const isToday = date === iso(new Date());

  return (
    <div className="px-4 pt-4 space-y-4" data-testid="m-today">
      <div className="flex items-center gap-2">
        <button onClick={() => setDate(shift(date, -1))} data-testid="m-day-prev" className="h-10 w-10 flex items-center justify-center bg-white border border-zinc-200"><ChevronLeft className="h-5 w-5" /></button>
        <label className="flex-1 relative h-10 bg-white border border-zinc-200 flex items-center justify-center text-sm font-medium text-[#0F172A]">
          {isToday ? "Today" : pretty(date)}
          <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} data-testid="m-day-date" className="absolute inset-0 opacity-0" />
        </label>
        <button onClick={() => setDate(shift(date, 1))} data-testid="m-day-next" className="h-10 w-10 flex items-center justify-center bg-white border border-zinc-200"><ChevronRight className="h-5 w-5" /></button>
      </div>
      {elevated && <RepPicker value={repId} onChange={setRepId} testid="m-day-rep" />}
      <QuickAddProspect date={date} repId={repId} onAdded={() => setReloadKey((k) => k + 1)} profileBase="/mobilecrm/customers" className="" />
      <div className="flex items-baseline justify-between">
        <h2 className="font-display font-bold text-lg text-[#0F172A]">{isToday ? "Today's stops" : pretty(date)}</h2>
        <span className="text-xs text-zinc-500" data-testid="m-day-progress">{doneCount}/{stops.length} done</span>
      </div>
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : stops.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-300 py-10 text-center text-sm text-zinc-500" data-testid="m-day-empty">
          <MapPin className="h-6 w-6 mx-auto mb-2 text-zinc-300" /> No visits or follow-ups for this day.
        </div>
      ) : (
        <div className="space-y-3">{stops.map((s) => <StopCard key={`${s.call_id}-${s.kind}`} s={s} onToggle={() => toggle(s)} showRep={repId === "all"} profileBase="/mobilecrm/customers" />)}</div>
      )}
    </div>
  );
}
